import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        snippets: {},
        loaders: {},
    };
}
const moduleBuilder = storeBuilder.module('course-snippets', makeInitialState());
/** The store for the courseSnippets. */
export var CourseSnippetsStore;
(function (CourseSnippetsStore) {
    /** Clear the entire CourseSnippetsStore. */
    CourseSnippetsStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    const commitSnippets = moduleBuilder.commit((state, { courseId, snippets, }) => {
        utils.vueSet(state.snippets, courseId, snippets);
    }, 'commitSnippets');
    /** Add a new snippet to the store. */
    CourseSnippetsStore.commitAddSnippet = moduleBuilder.commit((state, { courseId, snippet, }) => {
        const existing = state.snippets[courseId] ?? [];
        commitSnippets({
            courseId,
            snippets: Object.freeze([...existing, snippet]),
        });
    }, 'commitAddSnippet');
    /** Update a snippet in the store. */
    CourseSnippetsStore.commitUpdateSnippet = moduleBuilder.commit((state, { courseId, snippet, }) => {
        const existing = state.snippets[courseId] ?? [];
        commitSnippets({
            courseId,
            snippets: Object.freeze(existing.map(old => (old.id === snippet.id ? snippet : old))),
        });
    }, 'commitUpdateSnippet');
    /** Delete a snippet from the store. */
    CourseSnippetsStore.commitDeleteSnippet = moduleBuilder.commit((state, { courseId, snippet, }) => {
        const existing = state.snippets[courseId] ?? [];
        commitSnippets({
            courseId,
            snippets: Object.freeze(existing.filter(old => old.id !== snippet.id)),
        });
    }, 'commitDeleteSnippet');
    const commitLoader = moduleBuilder.commit((state, { courseId, loader, }) => {
        utils.vueSetOrDelete(state.loaders, courseId, loader);
    }, 'commitLoader');
    /** Get the snippets for a course. */
    CourseSnippetsStore.get = moduleBuilder.read(state => 
    /**
     * Get the snippets for a course.
     *
     * @param courseId The id of the course to get the snippets for.
     * @returns The snippets for the given course, or `Nothing` if the
     * snippets for that course have not yet been loaded.
     */
    (courseId) => Maybe.fromNullable(state.snippets[courseId]), 'getSnippets');
    /** Load the snippets for a course. */
    CourseSnippetsStore.load = moduleBuilder.dispatch(async (ctx, { courseId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitLoader({ courseId, loader: Maybe.of(loader) }),
            force,
            load: async () => {
                const snippets = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.course.getSnippets({
                    courseId,
                    nextToken: nextToken.extract(),
                })));
                commitSnippets({ courseId, snippets });
            },
            loader: Maybe.fromNullable(ctx.state.loaders[courseId]),
            removeLoader: () => commitLoader({ courseId, loader: Maybe.empty() }),
            value: Maybe.fromNullable(ctx.state.snippets[courseId]),
        });
    }, 'load');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        CourseSnippetsStore.commitClearAll();
    });
})(CourseSnippetsStore || (CourseSnippetsStore = {}));
